import styled from 'styled-components'
import { useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { getUnprocessableErrors } from 'utils/error'
import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Select } from 'components/v2/Select'
import { Label } from 'components/v2/Label'
import FormLayout from 'features/App/components/FormLayout'
import { VEHICLE_COMPONENT } from 'features/VehicleTemplates/components/Components/constants'
import { SEAT_NUMBERING } from 'features/VehicleTemplates/components/SeatNumbering/constants'
import { VehicleGridForm, updateSeatNumber } from 'features/VehicleTemplates/components/VehicleGridForm'
import { vehicleTypeMessages } from 'features/VehicleTemplates/components/Type/messages'
import { resolvers as loginResolvers } from 'resolvers/login'
import { getCapacity } from 'resolvers/vehicleTemplates'
import { schema } from './schema'
import { safeFormatMessage } from '@10xlabs/utils'
import { ChangeEvent, ReactElement } from 'react'
import { VehicleType } from 'gql/generated/graphql'

const CARS_COLUMNS_NB = 3
const VANS_COLUMNS_NB = 4
const BUS_COLUMNS_NB = 5

const VEHICLE_TYPE_LIST = [VehicleType.Bus, VehicleType.Van, VehicleType.Sedan, VehicleType.Suv]

const EMPTY_CELL = {
  component: VEHICLE_COMPONENT.EMPTY,
  properties: []
}

const seatNumberingMessages = defineMessages({
  [SEAT_NUMBERING.LEFT_TO_RIGHT]: {
    id: 'seatNumbering.leftToRight',
    defaultMessage: 'Left to right'
  },
  [SEAT_NUMBERING.RIGHT_TO_LEFT]: {
    id: 'seatNumbering.rightToLeft',
    defaultMessage: 'Right to left'
  },
  [SEAT_NUMBERING.TOP_TO_BOTTOM]: {
    id: 'seatNumbering.topToBottom',
    defaultMessage: 'Floor 2 first'
  },
  [SEAT_NUMBERING.BOTTOM_TO_TOP]: {
    id: 'seatNumbering.bottomToTop',
    defaultMessage: 'Floor 1 first'
  }
})

const messages = defineMessages({
  namePlaceholder: {
    id: 'dictionary.name',
    defaultMessage: 'Name'
  }
})

const CardContent = styled(FormLayout.CardContent)`
  max-width: 500px;
`

const GridLabelWrapper = styled.div<TSFixMe>`
  margin: 20px 0;
`

const FlexEndRow = styled(Row)<TSFixMe>`
  align-items: flex-end;
`

const getColumnCount = (vehicleType: VehicleType): number => {
  switch (vehicleType) {
    case VehicleType.Sedan:
    case VehicleType.Suv:
      return CARS_COLUMNS_NB
    case VehicleType.Van:
      return VANS_COLUMNS_NB
    default:
      return BUS_COLUMNS_NB
  }
}

export const createEmptyRow = (columnCount: TSFixMe): TSFixMe => {
  return new Array(columnCount).fill('').map((): TSFixMe => {
    return EMPTY_CELL
  })
}

const NewVehicleTemplateForm = (props: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const { values, touched, errors, handleChange, handleBlur, setFieldValue, handleSubmit, error, loading, isEdit } =
    props

  const handleTypeChange = ({ target: { value } }: ChangeEvent<HTMLSelectElement>): void => {
    setFieldValue('type', value)

    const newGrid = values.grid.map((floor: TSFixMe) => {
      return floor.map((row: TSFixMe) => {
        const columnsCount = getColumnCount(value as VehicleType)
        if (row.length > columnsCount) {
          return row.filter((_: TSFixMe, index: number) => {
            return index < getColumnCount(value as VehicleType)
          })
        } else if (row.length < columnsCount) {
          while (row.length < columnsCount) {
            row.push(EMPTY_CELL)
          }
        }
        return row
      })
    })
    setFieldValue('grid', newGrid)
  }

  const handleHorizontalSeatNumberingChange = ({ target: { value } }: TSFixMe): TSFixMe => {
    setFieldValue('horizontalSeatNumbering', value)

    const newGrid = updateSeatNumber(values.grid, value, values.verticalSeatNumbering)

    setFieldValue('grid', newGrid)
  }

  const handleVerticalSeatNumberingChange = ({ target: { value } }: TSFixMe): TSFixMe => {
    setFieldValue('verticalSeatNumbering', value)

    const newGrid = updateSeatNumber(values.grid, values.horizontalSeatNumbering, value)

    setFieldValue('grid', newGrid)
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <CardContent>
        <Row>
          <Column>
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            {isEdit ? (
              <Input
                value={safeFormatMessage({ intl, messages: vehicleTypeMessages, key: values.type })}
                label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
                readOnly
              />
            ) : (
              <Select
                id="type"
                value={values.type}
                onChange={handleTypeChange}
                onBlur={handleBlur}
                touched={touched.type}
                errors={errors.type}
                // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                readOnly={isEdit}
                label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
              >
                {VEHICLE_TYPE_LIST.map((type): TSFixMe => {
                  return (
                    <option key={type} value={type}>
                      {safeFormatMessage({ intl, messages: vehicleTypeMessages, key: type })}
                    </option>
                  )
                })}
              </Select>
            )}
          </Column>
        </Row>

        <FlexEndRow>
          {!isEdit && (
            <>
              <Column width="50%">
                <Select
                  id="horizontalSeatNumbering"
                  value={values.horizontalSeatNumbering}
                  onChange={handleHorizontalSeatNumberingChange}
                  onBlur={handleBlur}
                  label={<FormattedMessage id="dictionary.seatNumbering" defaultMessage="Seat numbering" />}
                >
                  <option value={SEAT_NUMBERING.LEFT_TO_RIGHT}>
                    {intl.formatMessage(seatNumberingMessages[SEAT_NUMBERING.LEFT_TO_RIGHT])}
                  </option>

                  <option value={SEAT_NUMBERING.RIGHT_TO_LEFT}>
                    {intl.formatMessage(seatNumberingMessages[SEAT_NUMBERING.RIGHT_TO_LEFT])}
                  </option>
                </Select>
              </Column>

              <Column width="50%">
                <Select
                  id="verticalSeatNumbering"
                  value={values.verticalSeatNumbering}
                  onChange={handleVerticalSeatNumberingChange}
                  onBlur={handleBlur}
                >
                  <option value={SEAT_NUMBERING.BOTTOM_TO_TOP}>
                    {intl.formatMessage(seatNumberingMessages[SEAT_NUMBERING.BOTTOM_TO_TOP])}
                  </option>

                  <option value={SEAT_NUMBERING.TOP_TO_BOTTOM}>
                    {intl.formatMessage(seatNumberingMessages[SEAT_NUMBERING.TOP_TO_BOTTOM])}
                  </option>
                </Select>
              </Column>
            </>
          )}
        </FlexEndRow>

        <Row>
          <Column width="50%">
            <Input
              value={getCapacity(values.grid)}
              label={<FormattedMessage id="dictionary.capacity" defaultMessage="Capacity" />}
              readOnly
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <GridLabelWrapper>
              <Label>
                <FormattedMessage id="vehicleTemplates.configureVehicle.label" defaultMessage="Configure Vehicle" />
              </Label>
            </GridLabelWrapper>

            <VehicleGridForm
              grid={values.grid}
              horizontalSeatNumbering={values.horizontalSeatNumbering}
              verticalSeatNumbering={values.verticalSeatNumbering}
              onChange={(grid: TSFixMe): TSFixMe => setFieldValue('grid', grid)}
              readOnly={isEdit}
            />
          </Column>
        </Row>
      </CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="vehicleTemplates.form.error"
                defaultMessage="There was an error while saving your template"
              />
            </ErrorMessage>
          </div>
        )}

        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/vehicle-templates" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewVehicleTemplate = ({ vehicleTemplate = {}, isEdit, onSubmit, error, loading }: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const type = vehicleTemplate.type || VehicleType.Bus
  const columnCount = getColumnCount(type)
  const defaultGrid = [[createEmptyRow(columnCount)]]

  const initialTemplate = {
    name: vehicleTemplate.name || '',
    type,
    horizontalSeatNumbering: vehicleTemplate.horizontalSeatNumbering || SEAT_NUMBERING.LEFT_TO_RIGHT,
    verticalSeatNumbering: vehicleTemplate.verticalSeatNumbering || SEAT_NUMBERING.BOTTOM_TO_TOP,
    grid: vehicleTemplate.layout || defaultGrid
  }

  return (
    <Formik initialValues={initialTemplate} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return <NewVehicleTemplateForm {...props} error={error} loading={loading} isEdit={isEdit} />
      }}
    </Formik>
  )
}
